import React, { useCallback, useEffect, useState } from 'react';

import { Button, Link, Typography } from '@vtblife/uikit';
import { PhoneInput, Size } from '@vtblife/uikit/legacy';

import { STATUS } from '@search/gql-client/src';
import type { QuizLastStep, QuizEmail } from '@search/graphql-typings';
import classname from '@search/classname/src';
import { useCallbackEmail } from '@search/vtbeco-frontend-core/domain/emails/useCallbackEmail';
import { dataLayerPush } from '@search/vtbeco-frontend-core/domain/google/dataLayerPush';
import { useInitialPhone } from '@search/vtbeco-frontend-core/view/common/hooks/useInitialPhone';

import { makeEmail, QuizStepResult } from '../service';

import QuizBuilderFinalStepAgreement from './QuizBuilderFinalStepAgreement';
import QuizBuilderFinalStepBonusList from './QuizBuilderFinalStepBonusList';

const cn = classname.bind(null, 'QuizBuilderFinalStep');

const MAIN_BUTTON_WIDTH = 296;
const ERROR_MESSAGE = 'Что-то пошло не так, попробуйте позже.';

export const QuizBuilderFinalStep = ({ lastStepData, result: quizResult, quizId, emailData, isMobile }: {
    lastStepData: QuizLastStep;
    result: Array<QuizStepResult>;
    emailData: QuizEmail;
    quizId: string;
    isMobile?: boolean;
}) => {
    const initialPhone = useInitialPhone();
    const [ phone, setPhone ] = useState<string>(initialPhone);
    const [ isAgree, setAgree ] = useState<boolean>(false);

    useEffect(() => setPhone(initialPhone), [ initialPhone ]);

    const [ requestStatus, setRequestStatus ] = useState<STATUS>(STATUS.INIT);

    const sendQuizRequest = useCallbackEmail();

    const sendQuiz = useCallback(() => {
        setRequestStatus(STATUS.PENDING);

        sendQuizRequest({
            subject: emailData.subject,
            body: makeEmail({ quizResult, phone, quizId }),
            emailRecipient: emailData.address
        }, { pathUniqSuffix: '/callback-quiz/' })
            .then(result => setRequestStatus(
                result.data?.createEmailCallbackSimple.emailMessageId ?
                    STATUS.RESOLVE :
                    STATUS.ERROR
            ))
            .catch(() => {
                setRequestStatus(STATUS.ERROR);
            });
    }, [ emailData.address, emailData.subject, phone, quizId, quizResult, sendQuizRequest ]);

    useEffect(() => {
        if (requestStatus === STATUS.ERROR) {
            // eslint-disable-next-line no-alert
            window.alert(ERROR_MESSAGE);
        }
    }, [ requestStatus ]);

    useEffect(() => {
        /* eslint-disable @typescript-eslint/naming-convention */
        requestStatus === STATUS.RESOLVE && dataLayerPush({
            quiz_id: quizId,
            event: 'nb_quiz_step_filled',
            event_action: 'success'
        });
        /* eslint-enable @typescript-eslint/naming-convention */
    }, [ requestStatus, quizId ]);

    return requestStatus === STATUS.RESOLVE ? (
        <div className={cn()}>
            <div className={cn('content')}>
                <Typography
                    variant='h2'
                    responsive={isMobile}
                >
                    Заявка на&nbsp;консультацию отправлена.<br />Скоро мы свяжемся с&nbsp;вами
                </Typography>
                <Typography
                    variant='primary'
                    className={cn('subtitle', { [isMobile ? 'primary' : 'secondary']: true })}
                >
                    Специальные предложения от&nbsp;m2.ru:
                </Typography>
                <QuizBuilderFinalStepBonusList
                    withPromoCode
                    bonusList={lastStepData.bonusList}
                    isMobile={isMobile}
                />
                <div className={cn('startNew')}>
                    <Typography variant='secondary-alone'>
                        Чтобы изменить параметры подбора, <Link onClick={() => window.location.reload()}>
                            <Typography variant='secondary-alone'>начните новый поиск</Typography>
                        </Link>
                    </Typography>
                </div>
            </div>
        </div>
    ) : (
        <div className={cn()}>
            <div className={cn('content')}>
                <Typography
                    variant='h2'
                    responsive={isMobile}
                >
                    {lastStepData.title}
                </Typography>
                <PhoneInput
                    fullWidth={isMobile}
                    value={phone}
                    onChange={setPhone}
                    hasClear
                    label='Номер телефона'
                    size={Size.Large}
                    className={cn('phone')}
                />
                <Typography
                    variant='primary'
                    className={cn('subtitle', { primary: true })}
                >
                    В&nbsp;подарок дадим промокоды на&nbsp;наши сервисы:
                </Typography>
                <QuizBuilderFinalStepBonusList
                    bonusList={lastStepData.bonusList}
                    isMobile={isMobile}
                />
                {! isMobile && lastStepData.withAgreement && (
                    <div className={cn('agreement')}>
                        <QuizBuilderFinalStepAgreement
                            value={isAgree}
                            handleChange={setAgree}
                        />
                    </div>
                )}
            </div>
            {isMobile && lastStepData.withAgreement && (
                <div className={cn('agreement')}>
                    <QuizBuilderFinalStepAgreement
                        value={isAgree}
                        handleChange={setAgree}
                    />
                </div>
            )}
            <div className={cn('button')}>
                <Button
                    fullWidth={isMobile}
                    disabled={phone.length < 11 || ! isAgree || requestStatus === STATUS.PENDING}
                    width={isMobile ? undefined : MAIN_BUTTON_WIDTH}
                    onClick={sendQuiz}
                >
                    {lastStepData.buttonText}
                </Button>
            </div>
        </div>
    );
};
